/**
 * EmptyValue (R16 T-137, design v1.2 change 04 §2) — the one way an empty value reads. In a record or a
 * form (inside a FieldGrid, or `variant="field"`): "Not set" in italic --ink-2 (information, so not
 * --ink-3, rule 26) and, when the person may edit, a quiet "+ Add" that opens the field's editor. In a
 * table cell: an em dash in --ink-2, because a column of "Not set" is noise. "Unassessed" (an empty
 * maturity) keeps its word everywhere, never a dash or a 0 (earlier rounds' rule; the repo wins).
 */
import { Button } from './Button';
import { useInFieldGrid } from './Field';
import './record.css';

export interface EmptyValueProps {
  /** "Not set" (default) or "Unassessed" for a maturity. */
  word?: string;
  /** Default: "field" inside a FieldGrid, "cell" elsewhere. */
  variant?: 'field' | 'cell';
  /** The person may edit: show "+ Add", which opens this field's editor. */
  onAdd?: () => void;
  /** Names the field for the "+ Add" button's accessible name ("Add Target date"). */
  fieldLabel?: string;
  title?: string;
  className?: string;
}

export function EmptyValue({ word = 'Not set', variant, onAdd, fieldLabel, title, className }: EmptyValueProps) {
  const inGrid = useInFieldGrid();
  const v = variant ?? (inGrid ? 'field' : 'cell');
  const cls = className ? `k-empty-value ${className}` : 'k-empty-value';
  if (v === 'cell' && word !== 'Unassessed') {
    return (
      <span data-kit="empty-value" data-variant="cell" className={cls} aria-label={word} title={title ?? word}>
        —
      </span>
    );
  }
  return (
    <span data-kit="empty-value" data-variant={v} className={cls} title={title}>
      <em>{word}</em>
      {onAdd ? (
        <Button variant="quiet" className="k-empty-add" onClick={onAdd} aria-label={fieldLabel ? `Add ${fieldLabel}` : undefined} data-testid="empty-value-add">
          + Add
        </Button>
      ) : null}
    </span>
  );
}
